import React, { useRef } from 'react';
import Button from '@atlaskit/button/new';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { Text, Stack, xcss, Box } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { cssMap } from '@atlaskit/css';
import { Flex } from '@atlaskit/primitives/compiled';
import {
	FireUiAnalytics,
	fireUIAnalytics,
	type UIAnalyticsEvent,
} from '@atlassian/jira-product-analytics-bridge';
import { useIntl, type MessageDescriptorV2 } from '@atlassian/jira-intl';
import { fg } from '@atlassian/jira-feature-gating';
import type { PopupHeight } from '../../../types.tsx';
import messages from './messages.tsx';

const POPUP_HEIGHT: PopupHeight = '380px';

export type ErrorType = 'fields' | 'fieldValues' | 'fieldValuesPagination' | 'fieldValuesSearch';
export type Spacing = 'default' | 'compact';

export type Props = {
	onClick: () => void;
	errorType: ErrorType;
	spacing?: Spacing;
	showSecondaryText?: boolean;
	buttonLabel?: MessageDescriptorV2;
};

const styles = cssMap({
	errorContainer: {
		paddingInline: token('space.250'),
		paddingBlock: token('space.250'),
		height: '480px',
		width: '100%',
	},
});

export const ErrorMessage = ({
	onClick,
	errorType,
	spacing = 'default',
	showSecondaryText = true,
	buttonLabel,
}: Props) => {
	const { formatMessage } = useIntl();

	const ref = useRef<HTMLDivElement>(null);

	return (
		<>
			<Stack
				alignInline="center"
				alignBlock="center"
				space={spacing === 'default' ? 'space.150' : 'space.0'}
				grow={
					fg('filter_refinement_m2_date_fields') || fg('list_lovability_improving_filters')
						? 'fill'
						: undefined
				}
				xcss={
					fg('filter_refinement_m2_date_fields') || fg('list_lovability_improving_filters')
						? containerStyles
						: [containerStyles, spacing === 'default' ? defaultStyles : compactStyles]
				}
				ref={ref}
				testId="filter-refinement.ui.filter-popup.filter-popup-content.ui.error-message.stack"
			>
				<StatusErrorIcon label="" />
				<Box>
					<Box paddingBlock="space.100" paddingInline="space.400" xcss={[textCenterStyles]}>
						<Text>{formatMessage(messages.errorPrimaryMessage)}</Text>
					</Box>
					{showSecondaryText && (
						<Box paddingBlock="space.100" paddingInline="space.400" xcss={[textCenterStyles]}>
							<Text size="small" color="color.text.subtle">
								{formatMessage(messages.errorSecondaryMessage)}
							</Text>
						</Box>
					)}
				</Box>
				<Button
					onClick={(_, analyticsEvent: UIAnalyticsEvent) => {
						fireUIAnalytics(analyticsEvent, 'filterDialogErrorRefreshButton', {
							errorType,
						});
						onClick();
					}}
					spacing={spacing}
					testId="filter-refinement.ui.filter-popup.filter-popup-content.ui.error-message.button"
				>
					{formatMessage(buttonLabel || messages.retryButtonLabel)}
				</Button>
			</Stack>
			<FireUiAnalytics
				actionSubject="errorMessage"
				action="viewed"
				actionSubjectId="filterDialogErrorMessage"
				attributes={{ errorType }}
			/>
		</>
	);
};

/* When the mode switcher is enabled, we need to wrap the error message in a container with fixed height.
 * This is beacause the popup container has no set height when the mode switcher is enabled as the height adjusts depending on each mode
 */
export const ErrorMessageWrapper = ({
	children,
	isModeSwitcherEnabled,
}: {
	children: React.ReactNode;
	isModeSwitcherEnabled: boolean | undefined;
}) => {
	if (isModeSwitcherEnabled && fg('list_lovability_improving_filters'))
		return <Flex xcss={styles.errorContainer}>{children}</Flex>;
	return children;
};

const containerStyles = xcss({
	color: 'color.text.subtle',
});

const defaultStyles = xcss({
	paddingBlock: 'space.250',
	height: POPUP_HEIGHT,
});

const compactStyles = xcss({
	paddingTop: 'space.100',
});

const textCenterStyles = xcss({
	textAlign: 'center',
});
